/* eslint-disable */
import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { useSelector, useDispatch } from 'react-redux';
import Switch from 'react-switch';

// utils / actions
import { updateAgent } from '../../../../actions/agent';
import { calculateTravelZips } from '../../../../common/utils/general';

// components
import {
  AgentInfo,
  AppointmentTypes,
  Days,
  Maps,
} from '../../../../common/views/appointment-profiles';
import { LoaderButton } from '../../../../common/components';

const DEFAULT_RADIUS_OPTIONS = [
  { value: 5, label: '5 miles' },
  { value: 10, label: '10 miles' },
  { value: 20, label: '20 miles' },
  { value: 30, label: '30 miles' },
  { value: 40, label: '40 miles' },
  { value: 50, label: '50 miles' },
];

function Row({ children, justifyBetween = false }) {
  return (
    <div className="row justify-content-center mb-5">
      <div className={`col-md-8 ${justifyBetween ? 'd-flex justify-content-between' : ''}`}>
        {children}
      </div>
    </div>
  );
}

function InfoBox({ children }) {
  return (
    <div className="row">
      <div className="d-flex col justify-content-center">
        <div className="py-1 px-3 border border-warning bg-warning-bg shadow-sm mb-3">
          {children}
        </div>
      </div>
    </div>
  );
}

function AppointmentsProfile() {
  const dispatch = useDispatch();
  const [ saving, setSaving ] = useState(false);
  const [ profileChangedSinceSave, setProfileChangedSinceSave ] = useState(false);
  const [ manualZipInputValid, setManualZipInputValid ] = useState(true);
  const {
    profile,
    awsConfig,
    agentMobilePhone,
  } = useSelector(store => store.appointments);
  const { selectedAgent } = useSelector(store => store.agents);
  const { accepting_appointments: acceptingAppointments } = selectedAgent;
  const { appointmentRadiusOptions = DEFAULT_RADIUS_OPTIONS } = useSelector(store => store.theme);

  const hasAppointmentsPerms = selectedAgent.can_edit_appointments_profile;
  const disabled = (!hasAppointmentsPerms || saving) ? true : false;

  function handleSave(aa) {
    setSaving(true);
    const canEditProfile = true; // admin can always edit profile regardless of agent perms
    const travelZips = calculateTravelZips(profile, canEditProfile);
    const updatedAppointmentsFlag = ((aa === undefined) ? acceptingAppointments : aa);
    const data = {
      appointments_profile: {
        ...profile,
        travelZips,
        lastSaved: moment.tz(moment(), moment.tz.guess()).toISOString(),
      },
      accepting_appointments: updatedAppointmentsFlag,
      mobile_phone: agentMobilePhone,
    };
    updateAgent(selectedAgent.username, data)
    .then((d) => {
      setProfileChangedSinceSave(false);
      dispatch({ type: 'SET_APPOINTMENTS', payload: { profile: d.appointments_profile } });
    }).finally(() => setSaving(false));
  }

  function handleSwitch() {
    setSaving(true);
    handleSave(!acceptingAppointments);
  }

  function setReduxProfileKeys(payload) {
    dispatch({ type: 'SET_APPOINTMENTS_PROFILE_KEYS', payload });
  }

  function setReduxAppointments(payload) {
    dispatch({ type: 'SET_APPOINTMENTS', payload });
  }

  function setReduxAgentMobilePhone(phone) {
    dispatch({ type: 'SET_APPOINTMENTS', payload: { agentMobilePhone: phone } });
  }

  useEffect(() => {
    setProfileChangedSinceSave(true);
  }, [profile]);

  useEffect(() => {
    dispatch({
      type: 'SET_APPOINTMENTS',
      payload: {
        agentMobilePhone: selectedAgent.mobile_phone,
        profile: selectedAgent.appointments_profile,
      },
    });
  }, []);

  return (
    <>
      {!hasAppointmentsPerms && (
        <InfoBox>
          This agent does not have permission enabled for this feature. Use the 'Site Access' tab to enable it.
        </InfoBox>
      )}
      <Row justifyBetween>
        {/* toggle switch */}
        <div style={{ position: 'relative' }}>
          <label className="d-flex align-items-center">
            <Switch
              onChange={() => handleSwitch()}
              checked={acceptingAppointments}
              disabled={saving || !hasAppointmentsPerms || !manualZipInputValid}
            />
            <span className="ms-2">
              {acceptingAppointments ? (
                <i>I am currently accepting leads</i>
              ) : (
                <i>I am <b>not</b> currently accepting leads</i>
              )}
            </span>
          </label>
        </div>

        {/* save button */}
        <div>
          <LoaderButton
            type="button"
            className="btn btn-outline-success"
            text={!profileChangedSinceSave ? 'Saved' : 'Save'}
            disabled={!profileChangedSinceSave || !hasAppointmentsPerms || !manualZipInputValid}
            loadingText="Saving..."
            loading={saving}
            onClick={() => handleSave()}
          />
        </div>
      </Row>
      <Row>
        <AgentInfo
          disabled={disabled}
          reduxProfile={profile || {}}
          setReduxProfileKeys={setReduxProfileKeys}
          reduxAgentMobilePhone={agentMobilePhone}
          setReduxAgentMobilePhone={setReduxAgentMobilePhone}
        />
      </Row>
      <Row>
        <AppointmentTypes
          disabled={disabled}
          reduxProfile={profile || {}}
          setReduxProfileKeys={setReduxProfileKeys}
        />
      </Row>
      <Row>
        <Days
          disabled={disabled}
          reduxProfile={profile || {}}
          setReduxProfileKeys={setReduxProfileKeys}
        />
      </Row>
      <Row>
        <Maps
          awsConfigUseDataSite
          allowManualZipEntry
          disabled={disabled}
          setReduxProfileKeys={setReduxProfileKeys}
          reduxProfile={profile || {}}
          canEditLocation
          setReduxAppointments={setReduxAppointments}
          awsConfig={awsConfig}
          radiusOptions={appointmentRadiusOptions}
          setManualZipInputValid={setManualZipInputValid}
          manualZipInputValid={manualZipInputValid}
        />
      </Row>
    </>
  );
}

export default AppointmentsProfile;
